import { DatePipe } from '@angular/common';
import {
    Component,
    ComponentRef,
    createEnvironmentInjector,
    EnvironmentInjector,
    Injectable,
    Input,
    NgModuleRef,
    Provider,
    Type,
    ViewContainerRef,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import type { CpUserControl, ICpEntity, IUserControlResolver } from '@se/cp.engine';
import { SeCpEngineModule } from '@se/cp.engine';

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'linked-ecg',
    template: '<div>X</div>',
    standalone: true,
    imports: [SeCpEngineModule],
})
export class EmptyUserControlComponent {
    @Input() control!: CpUserControl;
    @Input() dataRoot!: ICpEntity;
    @Input() form!: FormGroup;
}

@Injectable()
export class UserControlService implements IUserControlResolver {
    modules = new Map<string, NgModuleRef<any>>();

    constructor(private parentEnv: EnvironmentInjector) {}

    async loadComponent(vcr: ViewContainerRef, idUserControl: string): Promise<ComponentRef<any>> {
        switch (idUserControl) {
            case 'idUserControlLinkedEcgs':
                return this.createComponent(vcr, EmptyUserControlComponent);
            case 'idExampleUserControl':
                const { CpExampleUserControlComponent } = await import(
                    '@se/cp.usercontrols/cp-example-user-control/public-api'
                );
                return this.createComponent(vcr, CpExampleUserControlComponent);
            case 'idUserControlEcgData':
                const { CpUcEcgDataComponent } = await import('@se/cp.usercontrols/cp-uc-ecg-data/public-api');
                return this.createComponent(vcr, CpUcEcgDataComponent);
            case 'idUserControlDDeviceViewer':
                const { CpUcDDeviceViewerComponent } = await import(
                    '@se/cp.usercontrols/cp-uc-ddevice-viewer/public-api'
                );
                return this.createComponent(vcr, CpUcDDeviceViewerComponent);

            case 'idUserControlMeasureDetails':
                const { CpUcMeasureDetailsComponent } = await import(
                    '@se/cp.usercontrols/cp-uc-measure-details/public-api'
                );
                return this.createComponent(vcr, CpUcMeasureDetailsComponent);

            case 'idUserControlMeasureGrid': {
                const { CpUcMeasureGridComponent } = await import('@se/cp.usercontrols/cp-uc-measure-grid/public-api');
                const component = await this.createComponent(vcr, CpUcMeasureGridComponent);
                return Promise.resolve(component);
            }
            case 'idUserControlUs2ai': {
                const { CpUcUs2aiComponent } = await import('@se/cp.usercontrols/cp-uc-us2ai/public-api');
                const component = await this.createComponent(vcr, CpUcUs2aiComponent);
                return Promise.resolve(component);
            }
            case 'idUserControlSportVisit':
                const { CpUcSportVisitComponent } = await import('@se/cp.usercontrols/cp-uc-sport-visit/public-api');
                return this.createComponent(vcr, CpUcSportVisitComponent);
            case 'idUserControlSportImport':
                const { CpUcSportImportGridComponent, CpUcSportImportProviders } = await import(
                    '@se/cp.usercontrols/cp-uc-sport-import/public-api'
                );
                return this.createComponent(vcr, CpUcSportImportGridComponent, CpUcSportImportProviders);
            case 'idUserControlMultiStageMap':
                const { CpUcEcostressMapComponent } = await import(
                    '@se/cp.usercontrols/cp-uc-ecostress-map/public-api'
                );
                return this.createComponent(vcr, CpUcEcostressMapComponent);

            case 'idUserControlAddendum':
                const { CpUcAddendumComponent } = await import('@se/cp.usercontrols/cp-uc-addendum/public-api');
                return this.createComponent(vcr, CpUcAddendumComponent);

            case 'idUserControlClickDoc':
                const { CpUcClickDocComponent, CpUcClickDocProviders } = await import(
                    '@se/cp.usercontrols/cp-uc-click-doc/public-api'
                );
                return this.createComponent(vcr, CpUcClickDocComponent, CpUcClickDocProviders);

            case 'idUserControlCardioAtlas':
                const { CpUcCardioAtlasComponent } = await import('@se/cp.usercontrols/cp-uc-cardio-atlas/public.api');
                return this.createComponent(vcr, CpUcCardioAtlasComponent);

            case 'idUserControlDoseClasses':
                const { CpUcDoseClassesComponent } = await import('@se/cp.usercontrols/cp-uc-dose-classes/public-api');
                return this.createComponent(vcr, CpUcDoseClassesComponent);

            case 'idUserControlTechnicalExecutionData':
                const { CpUcTechnicalExecutionDataComponent } = await import(
                    '@se/cp.usercontrols/cp-uc-technical-execution-data/public-api'
                );
                return this.createComponent(vcr, CpUcTechnicalExecutionDataComponent, [DatePipe]);

            default:
                throw new Error('Error - No idUserControl');
        }
    }

    private async createComponent<C>(
        vcr: ViewContainerRef,
        component: Type<C>,
        providers?: Provider[],
    ): Promise<ComponentRef<C>> {
        vcr.clear();

        return vcr.createComponent(component, {
            index: 0,
            environmentInjector: this.createInjector(providers ?? [[]]),
        });
    }
    private createInjector(providers: Provider[]) {
        return createEnvironmentInjector(providers, this.parentEnv);
    }
}
